import React from 'react';
import { ChevronDown, DownloadCloud } from 'lucide-react';
import styled from '../../styled';
import { COLORS } from '../../themes/color';
import { AnimatedNumber, useInView, usePrefersReducedMotion } from '../motion';
import {
  AppMockupChrome,
  MerchantDot,
  ProductMockupBody,
  ProductMockupFrame
} from './shared';

const TitleRow = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;

const PageTitle = styled('div')`
  .title {
    font-size: 15px;
    font-weight: 700;
    color: #0e1c2b;
  }
`;

const ToolButton = styled('span')`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 500;
  color: #64748b;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 6px 10px;
  white-space: nowrap;
`;

const FilterRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;

const SkeletonControl = styled('span')`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 7px 10px;
`;

const ExportAction = styled('span')`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
  font-size: 11px;
  font-weight: 500;
  color: #94a3b8;
  white-space: nowrap;
`;

const SummaryCard = styled('div')`
  display: flex;
  overflow-x: auto;
  background: #fff;
  border: 1px solid #ececea;
  border-radius: 10px;
  padding: 12px 4px;
  margin-bottom: 12px;
`;

const SummaryMetricCell = styled('div')`
  flex: 1 0 auto;
  min-width: 96px;
  padding: 0 14px;
`;

const SummaryLabelRow = styled('div')`
  min-height: 14px;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  font-size: 10px;
  color: #94a3b8;
  white-space: nowrap;
`;

const SummaryValueRow = styled('div')`
  min-height: 18px;
  display: flex;
  align-items: center;
  font-size: 13.5px;
  font-weight: 700;
  color: #0e1c2b;
  white-space: nowrap;
`;

const TableCard = styled('div')`
  background: #fff;
  border: 1px solid #ececea;
  border-radius: 10px;
  overflow-x: auto;
`;

const Table = styled('table')`
  width: 100%;
  min-width: 820px;
  border-collapse: collapse;
  font-size: 11px;

  th {
    text-align: right;
    font-size: 9.5px;
    font-weight: 500;
    color: #b0b7c3;
    padding: 12px 10px 8px;
    white-space: nowrap;

    &.left {
      text-align: left;
    }

    &.skeleton {
      text-align: right;
    }
  }

  td {
    padding: 10px;
    color: #334155;
    white-space: nowrap;
    text-align: right;
    vertical-align: middle;

    &.left {
      text-align: left;
    }
  }
`;

const AnimatedRow = styled('tr')<{
  visible: boolean;
  delayMs: number;
  reduced: boolean;
}>`
  border-top: 1px solid #f4f4f2;
  opacity: ${(p) => (p.visible || p.reduced ? 1 : 0)};
  transform: ${(p) =>
    p.visible || p.reduced ? 'translateY(0)' : 'translateY(8px)'};
  transition: opacity 0.5s ease ${(p) => p.delayMs}ms,
    transform 0.5s cubic-bezier(0.22, 1, 0.36, 1) ${(p) => p.delayMs}ms;
`;

const CampaignCell = styled('div')`
  .name {
    font-size: 12px;
    font-weight: 600;
    color: #0e1c2b;
    margin-bottom: 5px;
  }

  .partner {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 10.5px;
    color: #475569;
  }
`;

const StatusPill = styled('span')<{ tone: 'success' | 'warning' | 'neutral' }>`
  display: inline-block;
  font-size: 10px;
  font-weight: 600;
  border-radius: 100px;
  padding: 3px 8px;
  color: ${(p) =>
    p.tone === 'success'
      ? '#389e0d'
      : p.tone === 'warning'
      ? '#d48806'
      : '#64748b'};
  background: ${(p) =>
    p.tone === 'success'
      ? '#f6ffed'
      : p.tone === 'warning'
      ? '#fffbe6'
      : '#f8fafc'};
`;

const ProgressCell = styled('div')`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  min-width: 108px;
`;

const ProgressTrack = styled('div')`
  width: 72px;
  height: 6px;
  border-radius: 100px;
  background: #eef1f4;
  overflow: hidden;
`;

const ProgressFill = styled('div')<{ pct: number }>`
  height: 100%;
  width: ${(p) => p.pct}%;
  border-radius: inherit;
  background: #2e5cff;
`;

const ProgressValue = styled('span')`
  font-size: 10.5px;
  font-weight: 600;
  color: #334155;
  min-width: 28px;
  text-align: right;
`;

const SkeletonBar = styled('span')<{ width: string }>`
  display: inline-block;
  height: 9px;
  border-radius: 100px;
  background: #eef1f4;
  width: ${(p) => p.width};
  vertical-align: middle;
`;

const SkeletonCampaignCell = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SkeletonRow = styled('tr')`
  border-top: 1px solid #f4f4f2;
`;

const featuredCampaign = {
  name: 'Holiday home goods',
  partner: 'Partner campaign',
  initial: 'H',
  color: COLORS.PRIMARY_DARK,
  status: 'On track' as const,
  progress: 88,
  spend: 142,
  earnings: 12.4
};

const filterSkeletonWidths = ['120px', '52px', '44px'];

const extraColumnHeaderWidths = [
  '48px',
  '44px',
  '52px',
  '40px',
  '56px',
  '46px'
];

const extraColumnCellWidths = [
  '44px',
  '52px',
  '38px',
  '48px',
  '42px',
  '50px'
];

const summarySkeletonWidths = ['56px', '48px', '52px', '44px'];

const skeletonNamedColumnWidths = [
  ['52px', '64px', '44px', '40px'],
  ['48px', '60px', '40px', '36px'],
  ['50px', '68px', '42px', '38px'],
  ['46px', '62px', '38px', '34px']
];

const skeletonCampaignWidths = ['128px', '116px', '122px', '108px'];

const summaryMetrics = [
  { label: 'Spend', value: 180, format: (n: number) => `$${Math.round(n)}K` },
  {
    label: 'Delivery',
    value: 88,
    format: (n: number) => `${Math.round(n)}%`
  },
  {
    label: 'Earnings',
    value: 55,
    format: (n: number) => `$${Math.round(n)}K`
  },
  { label: 'Campaigns', value: 12 }
];

export const CrossCampaignReportMockup = () => {
  const reduced = usePrefersReducedMotion();
  const { ref, inView } = useInView<HTMLDivElement>({ threshold: 0.2 });

  return (
    <ProductMockupFrame ref={ref}>
      <AppMockupChrome product="Amplify" surface="Reporting" />
      <ProductMockupBody>
        <TitleRow>
          <PageTitle>
            <span className="title">Campaign portfolio</span>
          </PageTitle>
          <ToolButton>
            Last 30 days <ChevronDown size={12} />
          </ToolButton>
        </TitleRow>

        <FilterRow>
          {filterSkeletonWidths.map((width) => (
            <SkeletonControl key={width}>
              <SkeletonBar width={width} />
              <ChevronDown size={12} color="#cbd5e1" />
            </SkeletonControl>
          ))}
          <ExportAction>
            <DownloadCloud size={12} /> Export
          </ExportAction>
        </FilterRow>

        <SummaryCard>
          {summaryMetrics.map((m) => (
            <SummaryMetricCell key={m.label}>
              <SummaryLabelRow>{m.label}</SummaryLabelRow>
              <SummaryValueRow>
                <AnimatedNumber
                  value={m.value}
                  start={inView}
                  format={m.format}
                />
              </SummaryValueRow>
            </SummaryMetricCell>
          ))}
          {summarySkeletonWidths.map((width) => (
            <SummaryMetricCell key={width}>
              <SummaryLabelRow>
                <SkeletonBar width={width} />
              </SummaryLabelRow>
              <SummaryValueRow>
                <SkeletonBar width="40px" />
              </SummaryValueRow>
            </SummaryMetricCell>
          ))}
        </SummaryCard>

        <TableCard>
          <Table>
            <thead>
              <tr>
                <th className="left">Campaign</th>
                <th className="left">Status</th>
                <th>Progress</th>
                <th>Spend</th>
                <th>Earnings</th>
                {extraColumnHeaderWidths.map((width) => (
                  <th key={width} className="skeleton">
                    <SkeletonBar width={width} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <AnimatedRow visible={inView} delayMs={200} reduced={reduced}>
                <td className="left">
                  <CampaignCell>
                    <div className="name">{featuredCampaign.name}</div>
                    <div className="partner">
                      <MerchantDot bg={featuredCampaign.color}>
                        {featuredCampaign.initial}
                      </MerchantDot>
                      {featuredCampaign.partner}
                    </div>
                  </CampaignCell>
                </td>
                <td className="left">
                  <StatusPill tone="success">{featuredCampaign.status}</StatusPill>
                </td>
                <td>
                  <ProgressCell>
                    <ProgressTrack>
                      <ProgressFill pct={featuredCampaign.progress} />
                    </ProgressTrack>
                    <ProgressValue>{featuredCampaign.progress}%</ProgressValue>
                  </ProgressCell>
                </td>
                <td>${featuredCampaign.spend}K</td>
                <td>${featuredCampaign.earnings}K</td>
                {extraColumnCellWidths.map((width) => (
                  <td key={width}>
                    <SkeletonBar width={width} />
                  </td>
                ))}
              </AnimatedRow>
              {skeletonNamedColumnWidths.map((widths, rowIndex) => (
                <SkeletonRow key={rowIndex}>
                  <td className="left">
                    <SkeletonCampaignCell>
                      <SkeletonBar width={skeletonCampaignWidths[rowIndex]} />
                      <SkeletonBar width="72px" />
                    </SkeletonCampaignCell>
                  </td>
                  {widths.map((width, cellIndex) => (
                    <td
                      key={cellIndex}
                      className={cellIndex === 0 ? 'left' : undefined}
                    >
                      <SkeletonBar width={width} />
                    </td>
                  ))}
                  {extraColumnCellWidths.map((width) => (
                    <td key={`${rowIndex}-${width}`}>
                      <SkeletonBar width={width} />
                    </td>
                  ))}
                </SkeletonRow>
              ))}
            </tbody>
          </Table>
        </TableCard>
      </ProductMockupBody>
    </ProductMockupFrame>
  );
};
